import {
  Aperture,
  CheckCircle2,
  Clock3,
  FolderClock,
  Image,
  Languages,
  LayoutGrid,
  Menu,
  Sparkles,
  UsersRound,
  Video,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useRenderQueue } from "../contexts/RenderQueueContext";

export default function Layout() {
  const { user } = useAuth();
  const { language, setLanguage, status, t } = useLanguage();
  const { items, dismiss } = useRenderQueue();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [queueOpen, setQueueOpen] = useState(true);
  const navItems = [
    {
      label: t({ tr: "Keşfet", en: "Explore" }),
      to: "/",
      icon: Sparkles,
      end: true,
    },
    {
      label: t({ tr: "Görseller", en: "Images" }),
      to: "/image-generator",
      icon: Image,
      end: false,
    },
    {
      label: t({ tr: "Video", en: "Video" }),
      to: "/video-generator",
      icon: Video,
      end: false,
    },
    {
      label: t({ tr: "Uygulamalar", en: "Apps" }),
      to: "/apps",
      icon: LayoutGrid,
      end: false,
    },
    {
      label: t({ tr: "Topluluk", en: "Community" }),
      to: "/community",
      icon: UsersRound,
      end: false,
    },
  ] as const;

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    if (menuOpen) document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [menuOpen]);

  const activeRenders = useMemo(
    () =>
      items.filter(
        (item) => !["completed", "failed", "timed_out"].includes(item.status),
      ).length,
    [items],
  );

  return (
    <div className="shell">
      <header className="nav">
        <div className="nav-inner">
          <Link
            className="brand"
            to="/"
            aria-label={t({ tr: "Dora AI ana sayfa", en: "Dora AI home" })}
          >
            <span className="brand-mark" aria-hidden="true">
              <svg viewBox="0 0 64 64" focusable="false">
                <path d="M17 14h14c11 0 18 7 18 18s-7 18-18 18H17V14Zm9 8v20h5c6 0 10-4 10-10s-4-10-10-10h-5Z" />
              </svg>
            </span>
            <span>Dora</span>
          </Link>

          <nav
            className="nav-links"
            aria-label={t({ tr: "Ana menü", en: "Primary navigation" })}
          >
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => (isActive ? "active" : "")}
              >
                <item.icon size={15} aria-hidden="true" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="nav-actions">
            {items.length > 0 && (
              <button
                className="icon-pill queue-trigger hide-mobile"
                type="button"
                onClick={() => setQueueOpen((value) => !value)}
                aria-expanded={queueOpen}
                aria-label={t({
                  tr: "Render kuyruğunu aç veya kapat",
                  en: "Toggle render queue",
                })}
              >
                <Aperture size={16} />
                {activeRenders > 0
                  ? t(
                      {
                        tr: "{{count}} işleniyor",
                        en: "{{count}} rendering",
                      },
                      { count: activeRenders },
                    )
                  : t({ tr: "Kuyruk", en: "Queue" })}
              </button>
            )}
            <button
              className="icon-pill language-toggle hide-mobile"
              type="button"
              onClick={() => setLanguage(language === "tr" ? "en" : "tr")}
              aria-label={t({
                tr: "Dili İngilizce yap",
                en: "Switch language to Turkish",
              })}
              title={t({
                tr: "English",
                en: "Türkçe",
              })}
            >
              <Languages size={15} />
              {language.toUpperCase()}
            </button>
            {user ? (
              <>
                <span className="credit-pill hide-mobile">
                  <Sparkles size={14} /> {user.creditBalance}
                  <span>{t({ tr: "kredi", en: "credits" })}</span>
                </span>
                <Link
                  className="icon-button hide-mobile"
                  to="/history"
                  aria-label={t({
                    tr: "Üretim geçmişi",
                    en: "Generation history",
                  })}
                  title={t({
                    tr: "Üretim geçmişi",
                    en: "Generation history",
                  })}
                >
                  <FolderClock size={18} />
                </Link>
                <Link className="profile-pill" to="/profile">
                  <span className="avatar" aria-hidden="true">
                    {user.username.slice(0, 1).toUpperCase()}
                  </span>
                  <span className="hide-narrow">{user.username}</span>
                </Link>
              </>
            ) : (
              <>
                <Link className="button ghost hide-mobile" to="/login">
                  {t({ tr: "Giriş yap", en: "Sign in" })}
                </Link>
                <Link className="button primary" to="/signup">
                  {t({ tr: "Üretmeye başla", en: "Start creating" })}
                </Link>
              </>
            )}
            <button
              className="icon-button mobile-toggle"
              type="button"
              aria-label={t({
                tr: "Menüyü aç",
                en: "Open navigation menu",
              })}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
            >
              <Menu size={20} />
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div
          className="mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-label={t({ tr: "Gezinme menüsü", en: "Navigation menu" })}
        >
          <button
            className="mobile-backdrop"
            type="button"
            aria-label={t({
              tr: "Menüyü kapat",
              en: "Close navigation menu",
            })}
            onClick={() => setMenuOpen(false)}
          />
          <aside className="mobile-drawer">
            <div className="mobile-drawer-head">
              <span className="brand">
                <span className="brand-mark" aria-hidden="true">
                  <svg viewBox="0 0 64 64" focusable="false">
                    <path d="M17 14h14c11 0 18 7 18 18s-7 18-18 18H17V14Zm9 8v20h5c6 0 10-4 10-10s-4-10-10-10h-5Z" />
                  </svg>
                </span>
                Dora
              </span>
              <button
                className="icon-button"
                type="button"
                aria-label={t({
                  tr: "Menüyü kapat",
                  en: "Close navigation menu",
                })}
                onClick={() => setMenuOpen(false)}
              >
                <X size={20} />
              </button>
            </div>
            {user && (
              <div className="mobile-account">
                <span className="avatar">{user.username[0].toUpperCase()}</span>
                <span>
                  <strong>{user.username}</strong>
                  <small>
                    {t(
                      {
                        tr: "{{count}} kredi kullanılabilir",
                        en: "{{count}} credits available",
                      },
                      { count: user.creditBalance },
                    )}
                  </small>
                </span>
              </div>
            )}
            <nav
              className="mobile-nav"
              aria-label={t({ tr: "Mobil menü", en: "Mobile navigation" })}
            >
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => (isActive ? "active" : "")}
                >
                  <item.icon size={19} />
                  {item.label}
                </NavLink>
              ))}
              {user && (
                <>
                  <NavLink to="/history">
                    <FolderClock size={19} />{" "}
                    {t({ tr: "Üretimlerim", en: "My creations" })}
                  </NavLink>
                  <NavLink to="/profile">
                    <Aperture size={19} />{" "}
                    {t({ tr: "Hesap ayarları", en: "Account settings" })}
                  </NavLink>
                </>
              )}
            </nav>
            <button
              className="mobile-language-toggle"
              type="button"
              onClick={() => setLanguage(language === "tr" ? "en" : "tr")}
            >
              <Languages size={18} />
              <span>
                {t({ tr: "Dil", en: "Language" })}
                <small>{language === "tr" ? "Türkçe" : "English"}</small>
              </span>
              <strong>{language === "tr" ? "EN" : "TR"}</strong>
            </button>
            {!user && (
              <div className="mobile-auth-actions">
                <Link className="button ghost" to="/login">
                  {t({ tr: "Giriş yap", en: "Sign in" })}
                </Link>
                <Link className="button primary" to="/signup">
                  {t({ tr: "Üretmeye başla", en: "Start creating" })}
                </Link>
              </div>
            )}
          </aside>
        </div>
      )}

      <Outlet />

      <footer className="footer">
        <div className="container footer-inner">
          <div>
            <Link className="brand" to="/">
              <span className="brand-mark" aria-hidden="true">
                <svg viewBox="0 0 64 64" focusable="false">
                  <path d="M17 14h14c11 0 18 7 18 18s-7 18-18 18H17V14Zm9 8v20h5c6 0 10-4 10-10s-4-10-10-10h-5Z" />
                </svg>
              </span>
              Dora
            </Link>
            <p>
              {t({
                tr: "Özel üret. Bilinçli paylaş.",
                en: "Create privately. Publish deliberately.",
              })}
            </p>
          </div>
          <nav aria-label={t({ tr: "Alt menü", en: "Footer navigation" })}>
            <Link to="/image-generator">
              {t({ tr: "Görseller", en: "Images" })}
            </Link>
            <Link to="/video-generator">{t({ tr: "Video", en: "Video" })}</Link>
            <Link to="/apps">{t({ tr: "Uygulamalar", en: "Apps" })}</Link>
            <Link to="/community">
              {t({ tr: "Topluluk", en: "Community" })}
            </Link>
          </nav>
        </div>
      </footer>

      {items.length > 0 && queueOpen && (
        <aside
          className="queue"
          aria-live="polite"
          aria-label={t({ tr: "Render kuyruğu", en: "Render queue" })}
        >
          <div className="queue-head">
            <span>
              <span className="queue-pulse" />
              <strong>{t({ tr: "Render kuyruğu", en: "Render queue" })}</strong>
            </span>
            <button
              className="icon-button"
              type="button"
              onClick={() => setQueueOpen(false)}
              aria-label={t({
                tr: "Render kuyruğunu kapat",
                en: "Close render queue",
              })}
            >
              <X size={16} />
            </button>
          </div>
          <div className="queue-list">
            {items.map((item) => {
              const complete = ["completed", "failed", "timed_out"].includes(
                item.status,
              );
              return (
                <div className="queue-item" key={item.id}>
                  <span className={`queue-status ${complete ? "done" : ""}`}>
                    {complete ? (
                      <CheckCircle2 size={16} />
                    ) : (
                      <Clock3 size={16} />
                    )}
                  </span>
                  <span className="queue-copy">
                    <strong>{item.modelVariant}</strong>
                    <small>{status(item.status)}</small>
                  </span>
                  {complete && (
                    <button
                      className="queue-dismiss"
                      type="button"
                      onClick={() => dismiss(item.id)}
                    >
                      {t({ tr: "Kaldır", en: "Dismiss" })}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </aside>
      )}
    </div>
  );
}
